import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * Password setup page — opened from the link in the setup email.
 * URL: /portal/setup?token=<one-time>&email=<customer-email>
 */
const CustomerPasswordSetup = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const email = params.get('email') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await axios.post('/customer/setup-password', { email, token, password });
      localStorage.setItem('customerToken', data.token);
      localStorage.setItem('customerEmail', data.customer.email);
      localStorage.setItem('customerName', data.customer.name);
      toast.success('Password set — welcome to your portal!');
      navigate('/portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center' }} data-testid="setup-invalid">
          <h2>Invalid setup link</h2>
          <p style={{ color: '#64748b' }}>Request a fresh link from the login page.</p>
          <Button onClick={() => navigate('/portal/login')}>Back to login</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16, padding: 32 }} data-testid="setup-card">
        <h1 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: 22, fontWeight: 700 }}>Set Your Password</h1>
        <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14 }}>
          You&apos;re setting up portal access for <strong>{email}</strong>.
        </p>
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>New password</label>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <KeyRound size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="setup-password-input"
              style={{ paddingLeft: 44, height: 48 }}
            />
          </div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Confirm password</label>
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <KeyRound size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              data-testid="setup-password-confirm"
              style={{ paddingLeft: 44, height: 48 }}
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="setup-submit"
            style={{ width: '100%', height: 48, background: 'linear-gradient(135deg,#e74c3c,#c0392b)', fontWeight: 600, fontSize: 16 }}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : (<>Save & Sign In <ArrowRight size={18} style={{ marginLeft: 8 }} /></>)}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CustomerPasswordSetup;
